import {
  ResponsiveDialogBody,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHero,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Badge } from '@/components/sui/badge'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useFormatNumber } from '@/hooks/use-format-number'
import { useShowBoletoCopy } from '@/hooks/use-show-boleto-copy'
import { PLAN_VISUAL_CONFIG, UNLIMITED_ENVELOPES } from '@/lib/plans'
import { cn } from '@/lib/utils'

import locales from '../locales'
import type { RecommendedSku } from '../plan-finder.utils'
import { getRecurrenceQuote } from '../plan-finder.utils'
import { RadioOptionCard } from '../radio-option-card'

interface ResultStepProps {
  recommended: RecommendedSku
  currency: string
  selectedRecurrence: 'Monthly' | 'Yearly'
  onSelectRecurrence: (recurrence: 'Monthly' | 'Yearly') => void
  savingsPercent: number
  isSeasonal: boolean
  onBack: () => void
  onConfirm: () => void
}

export function ResultStep({
  recommended,
  currency,
  selectedRecurrence,
  onSelectRecurrence,
  savingsPercent,
  isSeasonal,
  onBack,
  onConfirm,
}: ResultStepProps) {
  const { t } = useComponentTranslation(locales)
  const { formatCurrency, formatNumber } = useFormatNumber()
  const showBoletoCopy = useShowBoletoCopy()

  const yearlyTipKey = isSeasonal
    ? showBoletoCopy
      ? 'result.yearlyTipSeasonalBoleto'
      : 'result.yearlyTipSeasonal'
    : showBoletoCopy
      ? 'result.yearlyTipBoleto'
      : 'result.yearlyTip'

  const visual = PLAN_VISUAL_CONFIG[recommended.feature]
  const TierIcon = visual.icon

  // Price and envelopes come from the same quote, so a card can never state an
  // allowance its price does not pay for — the add-ons ride on both.
  const monthlyQuote = getRecurrenceQuote(recommended, 'Monthly')
  const yearlyQuote = getRecurrenceQuote(recommended, 'Yearly')
  const monthlyEnvelopes = monthlyQuote.envelopes
  const yearlyEnvelopes = yearlyQuote.envelopes

  const money = (cents: number) =>
    formatCurrency(cents / 100, currency, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })

  // A year of the monthly plan, add-ons and all — the only figure the yearly
  // total can honestly be struck through against.
  const yearlyAtMonthlyRate = monthlyQuote.price * 12
  // Derived from the two prices shown on this very row, so the badge can never
  // disagree with the strikethrough above it.
  const yearlySavings = yearlyAtMonthlyRate - yearlyQuote.price

  return (
    <>
      <ResponsiveDialogBody layout="centered">
        <ResponsiveDialogHero
          icon={
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl',
                visual.iconBg,
              )}
            >
              <TierIcon className={cn('h-6 w-6', visual.iconColor)} />
            </div>
          }
        >
          <ResponsiveDialogTitle className="text-2xl">
            {t('result.title', { plan: recommended.tierName })}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {t('result.subtitle')}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHero>

        <div className="mt-6 space-y-2">
          <RadioOptionCard
            align="start"
            selected={selectedRecurrence === 'Monthly'}
            onClick={() => onSelectRecurrence('Monthly')}
          >
            <div className="flex-1">
              <span className="text-sm font-medium">{t('result.monthly')}</span>
              <p className="text-muted-foreground text-xs">
                {monthlyEnvelopes === UNLIMITED_ENVELOPES
                  ? t('result.unlimitedEnvelopes')
                  : t('result.envelopesMonthly', {
                      amount: formatNumber(monthlyEnvelopes),
                    })}
              </p>
            </div>
            <span className="shrink-0 text-base font-bold">
              {money(monthlyQuote.price)}
              <span className="text-muted-foreground ml-0.5 text-xs font-normal">
                {t('result.perMonth')}
              </span>
            </span>
          </RadioOptionCard>

          <RadioOptionCard
            align="start"
            selected={selectedRecurrence === 'Yearly'}
            onClick={() => onSelectRecurrence('Yearly')}
            footer={
              <p className="text-muted-foreground text-[11px]">
                {t(yearlyTipKey, { percent: savingsPercent })}
              </p>
            }
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {t('result.yearly')}
                </span>
                {yearlySavings > 0 && (
                  <Badge variant="success" size="sm">
                    {t('result.saving', {
                      amount: formatCurrency(yearlySavings / 100, currency, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }),
                    })}
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground mt-1.5 text-xs">
                {yearlyEnvelopes === UNLIMITED_ENVELOPES
                  ? t('result.unlimitedEnvelopes')
                  : t('result.envelopesYearly', {
                      amount: formatNumber(yearlyEnvelopes),
                    })}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end">
              <span className="text-muted-foreground text-xs line-through">
                {money(yearlyAtMonthlyRate)}
              </span>
              <span className="text-base font-bold">
                {money(yearlyQuote.price)}
                <span className="text-muted-foreground ml-0.5 text-xs font-normal">
                  {t('result.perYear')}
                </span>
              </span>
            </div>
          </RadioOptionCard>
        </div>
      </ResponsiveDialogBody>

      <ResponsiveDialogFooter>
        <Button variant="outline" onClick={onBack}>
          {t('actions.back')}
        </Button>
        <Button onClick={onConfirm}>{t('result.selectPlan')}</Button>
      </ResponsiveDialogFooter>
    </>
  )
}
